import { File02 } from '@untitledui/icons';
import { memo, useCallback } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { cx } from '@/utils/cx';

export interface FileSidebarRowProps {
  fileId: string | number;
  filename: string;
  entityTypeLabel: string;
  /** True when this row's file is the active sidebar selection. */
  isActive: boolean;
  /** When set, render the multi-type indicator instead of the entity type. */
  isMultiType: boolean;
  mappedCount: number;
  totalColumns: number;
  onSelect: (fileId: string | number) => void;
}

/**
 * One row in the sidebar's file list. Extracted to its own memoized
 * component so the click handler stays a stable per-row reference (the
 * previous inline-arrow `onClick={() => onSelect(file.fileId)}` was a fresh
 * function every render inside the parent's .map, defeating React.memo).
 */
export const FileSidebarRow = memo(function FileSidebarRow({
  fileId,
  filename,
  entityTypeLabel,
  isActive,
  isMultiType,
  mappedCount,
  totalColumns,
  onSelect,
}: FileSidebarRowProps) {
  const handleClick = useCallback(() => onSelect(fileId), [onSelect, fileId]);

  let subtitle: string;
  if (isMultiType) {
    subtitle = `Multi-type · ${mappedCount}/${totalColumns} mapped`;
  } else {
    subtitle = `${entityTypeLabel} · ${mappedCount}/${totalColumns} mapped`;
  }

  return (
    <Button
      type='button'
      color='tertiary'
      size='sm'
      iconLeading={File02}
      onClick={handleClick}
      aria-pressed={isActive}
      aria-current={isActive ? 'true' : undefined}
      className={cx(
        'w-full items-start justify-start',
        isActive ? 'bg-bg-secondary text-fg-primary' : 'text-fg-secondary'
      )}
    >
      <div className='min-w-0 flex-1'>
        {/* The filename wraps rather than truncating — a long one was being cut
            mid-word with no ellipsis. Three things are needed, not one:
            `whitespace-normal` to beat the `whitespace-nowrap` inherited from
            Button's root; no `truncate` (it re-applies nowrap and its ellipsis
            never showed anyway, because the flex child overflowed its container
            rather than clipping inside it); and `break-all`, because import
            filenames are typically underscore-joined and underscores are not a
            reliable break opportunity across engines. A previous attempt added
            `text-wrap` alongside `truncate` — inert, since `white-space` wins
            over the `text-wrap` longhand. */}
        <div
          className={cx(
            'text-sm text-left whitespace-normal break-all',
            isActive ? 'font-medium text-fg-primary' : 'text-fg-primary'
          )}
          title={filename}
        >
          {filename}
        </div>
        {/* The subtitle is bounded ("Contact · 3/4 mapped"), so it keeps
            truncating — but needs the same nowrap override to truncate on one
            line rather than inherit Button's and overflow. */}
        <div className='mt-0.5 text-xs text-fg-quaternary truncate text-left whitespace-nowrap'>
          {subtitle}
        </div>
      </div>
    </Button>
  );
});
